import { ref, watch, type Ref } from 'vue'

import { createWorkflowWebSocket, getWorkflow } from '@/api/workflow'
import type { WorkflowInstance } from '@/types/workflow'

interface UseWorkflowLiveSyncOptions {
  projectId: number
  workflow: Ref<WorkflowInstance | null>
}

export function useWorkflowLiveSync(options: UseWorkflowLiveSyncOptions) {
  const workflowSocket = ref<WebSocket | null>(null)
  const socketReconnectTimer = ref<number | null>(null)
  const socketReady = ref(false)
  const socketUnavailable = ref(false)
  const socketEverOpened = ref(false)

  let pollTimer: number | null = null
  let backgroundPollTimer: number | null = null
  let pollInFlight = false

  const BACKGROUND_POLL_STATES = new Set([
    'confirmed',
    'in_progress',
    'revision_in_progress',
  ])

  function clearSocketReconnectTimer() {
    if (socketReconnectTimer.value !== null) {
      window.clearTimeout(socketReconnectTimer.value)
      socketReconnectTimer.value = null
    }
  }

  function disconnectWorkflowSocket() {
    clearSocketReconnectTimer()
    socketReady.value = false
    if (workflowSocket.value) {
      workflowSocket.value.close()
      workflowSocket.value = null
    }
    stopBackgroundPolling()
  }

  function shouldBackgroundPoll() {
    const state = String(options.workflow.value?.state || '').trim()
    return BACKGROUND_POLL_STATES.has(state)
  }

  function startBackgroundPolling() {
    if (backgroundPollTimer !== null) {
      return
    }

    backgroundPollTimer = window.setInterval(() => {
      void refreshLiveWorkflow()
    }, 5000)
  }

  function stopBackgroundPolling() {
    if (backgroundPollTimer !== null) {
      window.clearInterval(backgroundPollTimer)
      backgroundPollTimer = null
    }
  }

  function syncBackgroundPolling() {
    if (shouldBackgroundPoll()) {
      startBackgroundPolling()
      return
    }

    stopBackgroundPolling()
  }

  function connectWorkflowSocket() {
    disconnectWorkflowSocket()
    syncBackgroundPolling()

    if (socketUnavailable.value) {
      void refreshLiveWorkflow()
      return
    }

    try {
      const socket = createWorkflowWebSocket(options.projectId)
      workflowSocket.value = socket

      socket.onopen = () => {
        socketReady.value = true
        socketEverOpened.value = true
        socketUnavailable.value = false
      }

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data)
          if (message?.type === 'workflow_snapshot' && message.payload) {
            options.workflow.value = message.payload as WorkflowInstance
            syncBackgroundPolling()
            return
          }
          if (message?.type === 'error') {
            socket.close()
          }
        } catch {
          // Ignore malformed payloads and keep fallback polling available.
        }
      }

      socket.onclose = () => {
        socketReady.value = false
        workflowSocket.value = null
        clearSocketReconnectTimer()

        if (!socketEverOpened.value) {
          socketUnavailable.value = true
          void refreshLiveWorkflow()
          return
        }

        socketReconnectTimer.value = window.setTimeout(() => {
          connectWorkflowSocket()
        }, 2000)
      }

      socket.onerror = () => {
        socketReady.value = false
        if (!socketEverOpened.value) {
          socketUnavailable.value = true
        }
      }
    } catch {
      socketReady.value = false
      socketUnavailable.value = true
      void refreshLiveWorkflow()
    }
  }

  async function refreshLiveWorkflow() {
    if (pollInFlight) {
      return
    }

    pollInFlight = true
    try {
      options.workflow.value = await getWorkflow(options.projectId)
      syncBackgroundPolling()
    } catch {
      // Keep polling silently while generation is running.
    } finally {
      pollInFlight = false
    }
  }

  function startPolling() {
    if (socketReady.value) {
      return
    }

    stopPolling()
    void refreshLiveWorkflow()
    pollTimer = window.setInterval(() => {
      void refreshLiveWorkflow()
    }, 1500)
  }

  function stopPolling() {
    if (pollTimer !== null) {
      window.clearInterval(pollTimer)
      pollTimer = null
    }
  }

  watch(
    () => String(options.workflow.value?.state || '').trim(),
    () => {
      syncBackgroundPolling()
    },
    { immediate: true }
  )

  return {
    connectWorkflowSocket,
    disconnectWorkflowSocket,
    startPolling,
    stopPolling,
  }
}